import { useOnboardingGoals } from '@/components/Onboarding/hooks/useOnboardingGoals';
import { useVisibleTasksList } from '@/components/Onboarding/hooks/useVisibleTasksList';
import { OnboardingStepItem, OnboardingTask } from '@/components/Onboarding/types';
import { sendEventToGTM } from '@dev/ee-tools';

const isActiveStepOutOfBounds = (task: OnboardingTask) =>
    task.activeStepsIndex < 0 || task.activeStepsIndex >= task.steps.length;
const getCompletedSteps = (item: OnboardingStepItem) => item.isCompleted;
const getCounters = (task: OnboardingTask) => {
    const activeStep = task.steps[task.activeStepsIndex];
    return { itemCount: activeStep.items.length, completedCount: activeStep.items.filter(getCompletedSteps).length };
};

/**
 * Calculates the total number of steps and the number of completed steps in a given list of onboarding tasks.
 * For tasks with multiple steps, the function considers the active step number of items as the contribution to the total steps.
 * Each item within an active step is counted towards the total step count, and if an item is marked as completed, it is also counted towards the completed steps.
 *
 * @param onboardingTasksList - An array of onboarding tasks, where each task contains one or more steps, and each step contains one or more items.
 * @returns An object containing two properties: totalSteps (the total number of steps across all tasks) and completedSteps (the number of steps that have been completed).
 */
const calculateSteps = (onboardingTasksList: OnboardingTask[]) =>
    onboardingTasksList.reduce(
        ({ totalSteps, completedSteps }, task) => {
            // Early return if the activeStepsIndex is out of bounds
            if (isActiveStepOutOfBounds(task)) return { totalSteps, completedSteps };
            const { itemCount, completedCount } = getCounters(task);
            return {
                totalSteps: totalSteps + itemCount,
                completedSteps: completedSteps + completedCount,
            };
        },
        { totalSteps: 0, completedSteps: 0 }
    );
export const useGA4SkipEvent = () => {
    const onboardingGoals = useOnboardingGoals();
    const onboardingTasksList = useVisibleTasksList(onboardingGoals);
    const { totalSteps, completedSteps } = calculateSteps(onboardingTasksList);
    const completedStepsPercentage = `${Math.round((completedSteps / totalSteps) * 100)}%`;

    return () =>
        sendEventToGTM('onboarding_skipped', {
            completedStepsPercentage,
            completedSteps,
        });
};
